import { stateKey } from 'config/constants'

const KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  EXPIRES_IN: 'expires_in',
  USER_ID: 'user_id',
  AGENT_ID: 'agent_id',
  TOKEN_UPDATED_AT: 'token_updated_at',
  LAST_ACTIVITY_AT: 'last_activity_at',
  TOKEN_REFRESH_LOCK: 'token_refresh_lock'
}

export const ACCESS_TOKEN_KEY = KEYS.ACCESS_TOKEN
export const LAST_ACTIVITY_KEY = KEYS.LAST_ACTIVITY_AT

const TOKEN_REFRESH_LOCK_TTL_MS = 15000
const ACTIVITY_WRITE_INTERVAL_MS = 1000

const BROKEN_PERSIST_KEY = '[object Object]'

const hasValue = value =>
  value != null &&
  value !== '' &&
  value !== 'null' &&
  value !== 'undefined'

export const persistAgentIdState = id => {
  if (!hasValue(id)) {
    return
  }

  localStorage.setItem(stateKey, JSON.stringify({ agentId: String(id) }))
  localStorage.removeItem(BROKEN_PERSIST_KEY)
}

export const getPersistedAgentId = () => {
  try {
    const raw = localStorage.getItem(stateKey)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw)
    const id =
      parsed?.agentId ||
      parsed?.auth?.data?.current_user?.id ||
      parsed?.auth?.data?.user?.id ||
      parsed?.user?.current_user?.id ||
      parsed?.user?.id ||
      null

    return hasValue(id) ? String(id) : null
  } catch {
    return null
  }
}

export const getAccessToken = () => localStorage.getItem(KEYS.ACCESS_TOKEN)

export const getRefreshToken = () => localStorage.getItem(KEYS.REFRESH_TOKEN)

export const getExpiresIn = () => {
  const raw = localStorage.getItem(KEYS.EXPIRES_IN)
  return raw ? Number.parseInt(raw, 10) : null
}
export const getTokenUpdatedAt = () => {
  const raw = localStorage.getItem(KEYS.TOKEN_UPDATED_AT)
  return raw ? Number.parseInt(raw, 10) : null
}

export const getLastActivityAt = () => {
  const raw = localStorage.getItem(KEYS.LAST_ACTIVITY_AT)
  return raw ? Number.parseInt(raw, 10) : null
}

export const setLastActivityAt = (timestamp = Date.now()) => {
  localStorage.setItem(KEYS.LAST_ACTIVITY_AT, String(timestamp))
  return timestamp
}

export const touchLastActivityAt = (
  minIntervalMs = ACTIVITY_WRITE_INTERVAL_MS
) => {
  const now = Date.now()
  const last = getLastActivityAt()
  if (last != null && now - last < minIntervalMs) {
    return last
  }
  return setLastActivityAt(now)
}

export const tryAcquireTokenRefreshLock = () => {
  const now = Date.now()
  const raw = localStorage.getItem(KEYS.TOKEN_REFRESH_LOCK)
  const lockedAt = raw ? Number.parseInt(raw, 10) : 0
  if (lockedAt && now - lockedAt < TOKEN_REFRESH_LOCK_TTL_MS) {
    return false
  }
  localStorage.setItem(KEYS.TOKEN_REFRESH_LOCK, String(now))
  return true
}

export const releaseTokenRefreshLock = () => {
  localStorage.removeItem(KEYS.TOKEN_REFRESH_LOCK)
}

export const persistUserId = id => {
  if (!hasValue(id)) {
    return false
  }

  const value = String(id)
  localStorage.setItem(KEYS.USER_ID, value)
  localStorage.setItem(KEYS.AGENT_ID, value)
  persistAgentIdState(value)
  return true
}

export const getUserId = () => {
  const fromKeys =
    localStorage.getItem(KEYS.USER_ID) || localStorage.getItem(KEYS.AGENT_ID)

  if (hasValue(fromKeys)) {
    return fromKeys
  }

  const fromPersist = getPersistedAgentId()
  if (hasValue(fromPersist)) {
    persistUserId(fromPersist)
    return fromPersist
  }

  return null
}

export const resolveUserIdFromPayload = data => {
  if (!data || typeof data !== 'object') {
    return null
  }

  return (
    data.user?.id ||
    data.current_user?.id ||
    data.user_id ||
    data.agent_id ||
    null
  )
}

export const getStoredAuth = () => ({
  accessToken: getAccessToken(),
  refreshToken: getRefreshToken(),
  expiresIn: getExpiresIn(),
  userId: getUserId()
})

export const setAuthTokens = data => {
  if (!data || typeof data !== 'object') {
    return
  }

  const { access_token, refresh_token, expires_in } = data

  if (hasValue(access_token)) {
    localStorage.setItem(KEYS.ACCESS_TOKEN, access_token)
    localStorage.setItem(KEYS.TOKEN_UPDATED_AT, String(Date.now()))
  }

  if (hasValue(refresh_token)) {
    localStorage.setItem(KEYS.REFRESH_TOKEN, refresh_token)
  }

  if (hasValue(expires_in)) {
    localStorage.setItem(KEYS.EXPIRES_IN, String(expires_in))
  }

  persistUserId(resolveUserIdFromPayload(data) || getUserId())
}

export const clearAuthTokens = () => {
  localStorage.removeItem(KEYS.ACCESS_TOKEN)
  localStorage.removeItem(KEYS.REFRESH_TOKEN)
  localStorage.removeItem(KEYS.EXPIRES_IN)
  localStorage.removeItem(KEYS.USER_ID)
  localStorage.removeItem(KEYS.AGENT_ID)
  localStorage.removeItem(KEYS.TOKEN_UPDATED_AT)
  localStorage.removeItem(KEYS.LAST_ACTIVITY_AT)
  localStorage.removeItem(KEYS.TOKEN_REFRESH_LOCK)
  localStorage.removeItem(stateKey)
  localStorage.removeItem(BROKEN_PERSIST_KEY)
}

if (typeof localStorage !== 'undefined' && getAccessToken()) {
  getUserId()
}
